import React, { useState, useMemo, useRef, useEffect } from "react";
import { CTBReport } from "../../../utils/api/endpoints/reports/parseReports";
import { BiSortAlt2, BiFilter } from "react-icons/bi";
import { parseCategoryString } from "../../../utils/categoryUtils";
import {
  FaAngleDown,
  FaSearch,
  FaTimes,
  FaFilter,
  FaProjectDiagram,
  FaClipboardList,
  FaExclamationTriangle,
  FaUserShield,
  FaSort,
  FaSortAmountDown,
  FaSortAmountUp,
  FaFolderOpen,
  FaArchive,
  FaLock,
  FaColumns
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import { cropSentence } from "../../../utils/formatText";
import OutlineButton from "../../buttons/OutlineButton";
import { useNavigate } from "react-router-dom";
import { FiColumns, FiChevronDown, FiPlus, FiSettings, FiChevronRight, FiTrash2, FiSearch, FiDownload } from "react-icons/fi";
import apiInstance from "../../../utils/api/axios";
import toast from "react-hot-toast";



// Add ColumnVisibility type
type ColumnVisibility = {
  createdAt: boolean;
  pentester: boolean;
  action: boolean;
  assetType: boolean;
};

// Update the filters state types
interface FilterState {
  programs: string[];
  states: string[];
  severityCategories: string[];
  pentesters: string[];
}

interface FilterSectionReportsProps {
  reports: CTBReport[];
  programNames: { id: number; name: string }[];
  vulnerabilityTypes: string[];
  reportStates: string[];
  severityCategories: string[];
  pentesters: string[];
  filters: {
    searchQuery: string;
    program: string;
    reportState: string;
    severityCategory: string;
    vulnerabilityType: string;
    sortOrder: string;
    pentesterUsername: string;
    includeArchived?: boolean | string;
    includeClosed?: boolean;
  };
  onFilter: (filters: {
    searchQuery: string;
    program: string;
    reportState: string;
    severityCategory: string;
    vulnerabilityType: string;
    sortOrder: string;
    pentesterUsername: string;
    includeArchived?: boolean | string;
    includeClosed?: boolean;
  }) => void;
  columnVisibility: ColumnVisibility;
  onColumnVisibilityChange: (visibility: ColumnVisibility) => void;
}

const FilterSectionReports: React.FC<FilterSectionReportsProps> = ({
  reports,
  programNames,
  vulnerabilityTypes,
  reportStates,
  severityCategories,
  pentesters,
  filters,
  onFilter,
  columnVisibility,
  onColumnVisibilityChange
}) => {

  const [searchQuery, setSearchQuery] = useState(filters.searchQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [programId, setProgramId] = useState(filters.program);
  const [programName, setProgramName] = useState("");
  const [reportState, setReportState] = useState<string[]>(
    filters.reportState ? filters.reportState.split(",") : []
  );
  const [severityCategory, setSeverityCategory] = useState(
    filters.severityCategory
  );
  const [vulnerabilityType, setVulnerabilityType] = useState(
    filters.vulnerabilityType
  );
  const [sortOrder, setSortOrder] = useState(filters.sortOrder);
  const [pentesterUsername, setPentesterUsername] = useState(
    filters.pentesterUsername
  );
  const [pentesterSearchQuery, setPentesterSearchQuery] = useState("");
  const [tempProgramId, setTempProgramId] = useState(filters.program);
  const [tempProgramName, setTempProgramName] = useState(
    programNames.find(p => p.id.toString() === filters.program)?.name || ""
  );
  const [tempReportState, setTempReportState] = useState<string[]>(
    filters.reportState ? filters.reportState.split(",") : []
  );
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [tempPentesterUsername, setTempPentesterUsername] = useState(
    filters.pentesterUsername
  );
  const [isStatesOpen, setIsStatesOpen] = useState(false);
  const [programSearchQuery, setProgramSearchQuery] = useState("");
  const { role } = useUserCredentials();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await apiInstance.get(`/v2/reports/export`, {
        params: filters,
        responseType: "blob"
      });

      const contentDisposition = response.headers ? response.headers["content-disposition"] : null;
      let filename = `vulnerabilities_export.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Excel export downloaded successfully.");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export vulnerabilities. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    programs: filters.program ? [filters.program] : [],
    states: filters.reportState ? filters.reportState.split(",") : [],
    severityCategories: filters.severityCategory
      ? [filters.severityCategory]
      : [],
    pentesters: filters.pentesterUsername ? [filters.pentesterUsername] : []
  });

  const [includeArchived, setIncludeArchived] = useState<string>(String(filters.includeArchived || "false"));
  const [includeClosed, setIncludeClosed] = useState(filters.includeClosed || false);

  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const addFilterRef = useRef<HTMLDivElement>(null);

  const statusModalRef = useRef<HTMLDivElement>(null);
  const substatusModalRef = useRef<HTMLDivElement>(null);
  const pentesterModalRef = useRef<HTMLDivElement>(null);
  const togglePrograms = () => setIsProgramsOpen(!isProgramsOpen);
  const toggleStates = () => setIsStatesOpen(!isStatesOpen);

  // Use the filter options from the API
  const uniqueReportStates = reportStates;
  const uniqueSeverityCategories = severityCategories;
  const uniquePentesters = pentesters;

  const filteredPentesters = useMemo(() => {
    return uniquePentesters.filter(
      username =>
        username &&
        username.toLowerCase().includes(pentesterSearchQuery.toLowerCase())
    );
  }, [uniquePentesters, pentesterSearchQuery]);

  const [openSection, setOpenSection] = useState<string | null>(null);

  // Add refs for all dropdowns
  const programsDropdownRef = useRef<HTMLDivElement>(null);
  const statesDropdownRef = useRef<HTMLDivElement>(null);
  const severityDropdownRef = useRef<HTMLDivElement>(null);
  const pentestersDropdownRef = useRef<HTMLDivElement>(null);
  const columnsDropdownRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle click outside for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle Add Filter popover
      if (
        addFilterRef.current &&
        !addFilterRef.current.contains(event.target as Node)
      ) {
        setIsAddFilterOpen(false);
        setActiveSubMenu(null);
      }

      // Handle all other dropdowns
      const dropdownRefs = {
        programs: programsDropdownRef,
        states: statesDropdownRef,
        severity: severityDropdownRef,
        pentesters: pentestersDropdownRef,
        columns: columnsDropdownRef
      };

      const clickedButton = Object.entries(buttonRefs.current).find(
        ([_, button]) => button?.contains(event.target as Node)
      );

      if (!clickedButton) {
        Object.entries(dropdownRefs).forEach(([key, ref]) => {
          if (ref.current && !ref.current.contains(event.target as Node)) {
            if (key === "columns") {
              setIsColumnMenuOpen(false);
            } else {
              setOpenSection(prev => (prev === key ? null : prev));
            }
          }
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync temporary states with applied filters
  useEffect(() => {
    setTempProgramId(filters.program);
    setTempProgramName(
      programNames.find(p => p.id.toString() === filters.program)?.name || ""
    );
    setTempReportState(
      filters.reportState ? filters.reportState.split(",") : []
    );
    setTempPentesterUsername(filters.pentesterUsername);
    
    // Sync main local states to ensure UI is in sync with parent
    setSearchQuery(filters.searchQuery);
    setProgramId(filters.program);
    setReportState(filters.reportState ? filters.reportState.split(",") : []);
    setSeverityCategory(filters.severityCategory);
    setVulnerabilityType(filters.vulnerabilityType);
    setSortOrder(filters.sortOrder);
    setPentesterUsername(filters.pentesterUsername);
    setIncludeArchived(String(filters.includeArchived || "false"));
    setIncludeClosed(filters.includeClosed || false);
    
    // Sync selectedFilters object
    setSelectedFilters({
      programs: filters.program ? filters.program.split(",") : [],
      states: filters.reportState ? filters.reportState.split(",") : [],
      severityCategories: filters.severityCategory ? filters.severityCategory.split(",") : [],
      pentesters: filters.pentesterUsername ? filters.pentesterUsername.split(",") : []
    });
  }, [filters, programNames]);



  // Update filter handlers to support multi-select
  const handleProgramToggle = (programName: string) => {
    const selectedProgram = programNames.find(p => p.name === programName);
    const programId = selectedProgram ? selectedProgram.id.toString() : "";

    setSelectedFilters(prev => {
      const newPrograms = prev.programs.includes(programId)
        ? prev.programs.filter(id => id !== programId)
        : [...prev.programs, programId];

      const newFilters = { ...prev, programs: newPrograms };

      // Apply filters immediately
      onFilter({
        searchQuery,
        program: newPrograms.join(","),
        reportState: prev.states.join(","),
        severityCategory: prev.severityCategories.join(","),
        vulnerabilityType,
        sortOrder,
        pentesterUsername: prev.pentesters.join(",")
      });

      return newFilters;
    });
  };

  const handleStateToggle = (state: string) => {
    setSelectedFilters(prev => {
      const newStates = prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state];

      const newFilters = { ...prev, states: newStates };

      // Apply filters immediately
      onFilter({
        searchQuery,
        program: prev.programs.join(","),
        reportState: newStates.join(","),
        severityCategory: prev.severityCategories.join(","),
        vulnerabilityType,
        sortOrder,
        pentesterUsername: prev.pentesters.join(",")
      });

      return newFilters;
    });
  };

  const handleSeverityToggle = (category: string) => {
    setSelectedFilters(prev => {
      const newCategories = prev.severityCategories.includes(category)
        ? prev.severityCategories.filter(c => c !== category)
        : [...prev.severityCategories, category];

      const newFilters = { ...prev, severityCategories: newCategories };

      // Apply filters immediately
      onFilter({
        searchQuery,
        program: prev.programs.join(","),
        reportState: prev.states.join(","),
        severityCategory: newCategories.join(","),
        vulnerabilityType,
        sortOrder,
        pentesterUsername: prev.pentesters.join(",")
      });

      return newFilters;
    });
  };

  const handlePentesterToggle = (username: string) => {
    setSelectedFilters(prev => {
      const newPentesters = prev.pentesters.includes(username)
        ? prev.pentesters.filter(p => p !== username)
        : [...prev.pentesters, username];

      const newFilters = { ...prev, pentesters: newPentesters };

      // Apply filters immediately
      onFilter({
        searchQuery,
        program: prev.programs.join(","),
        reportState: prev.states.join(","),
        severityCategory: prev.severityCategories.join(","),
        vulnerabilityType,
        sortOrder,
        pentesterUsername: newPentesters.join(",")
      });

      return newFilters;
    });
  };

  const handleApplyFilter = () => {
    onFilter({
      searchQuery,
      program: selectedFilters.programs.join(","),
      reportState: selectedFilters.states.join(","),
      severityCategory: selectedFilters.severityCategories.join(","),
      vulnerabilityType,
      sortOrder,
      pentesterUsername: selectedFilters.pentesters.join(","),
      includeArchived,
      includeClosed
    });
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);
    onFilter({
      searchQuery: newSearchQuery,
      program: selectedFilters.programs.join(","),
      reportState: selectedFilters.states.join(","),
      severityCategory: selectedFilters.severityCategories.join(","),
      vulnerabilityType,
      sortOrder,
      pentesterUsername: selectedFilters.pentesters.join(","),
      includeArchived,
      includeClosed
    });
  };

  const handlePentesterChange = (username: string) => {
    setTempPentesterUsername(username);
  };

  const handleSeverityCategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newSeverityCategory = event.target.value;
    setSeverityCategory(newSeverityCategory);
    onFilter({
      searchQuery,
      program: programId,
      reportState: reportState.join(","),
      severityCategory: newSeverityCategory,
      vulnerabilityType,
      sortOrder,
      pentesterUsername
    });
  };

  const filteredPrograms = useMemo(() => {
    return programNames.filter(program =>
      program.name.toLowerCase().includes(programSearchQuery.toLowerCase())
    );
  }, [programNames, programSearchQuery]);

  const handleSortOrderChange = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
    onFilter({
      searchQuery,
      program: programId,
      reportState: reportState.join(","),
      severityCategory,
      vulnerabilityType,
      sortOrder: newSortOrder,
      pentesterUsername
    });
  };

  // Update clear filters function
  const handleClearAll = () => {
    setSelectedFilters({
      programs: [],
      states: [],
      severityCategories: [],
      pentesters: []
    });
    setSearchQuery("");
    setVulnerabilityType("");
    setSortOrder("desc");

    onFilter({
      searchQuery: "",
      program: "",
      reportState: "",
      severityCategory: "",
      vulnerabilityType: "",
      sortOrder: "desc",
      pentesterUsername: "",
      includeArchived: "false",
      includeClosed: false
    });
    setIncludeArchived("false");
    setIncludeClosed(false);
  };

  const navigate = useNavigate();

  const handleClearSelected = () => {
    setTempProgramId("");
    setTempProgramName("");
    setTempReportState([]);
    setProgramId("");
    setProgramName("");
    setReportState([]);

    onFilter({
      searchQuery,
      program: "",
      reportState: "",
      severityCategory,
      vulnerabilityType,
      sortOrder,
      pentesterUsername
    });
  };

  const handleColumnVisibilityChange = (column: keyof ColumnVisibility) => {
    const newVisibility = { ...columnVisibility };
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;

    // If trying to show a column
    if (!columnVisibility[column]) {
      // Only allow if we have less than 2 visible columns
      if (currentVisibleCount < 2) {
        newVisibility[column] = true;
        onColumnVisibilityChange(newVisibility);
      }
    } else {
      // If trying to hide a column, just hide it
      newVisibility[column] = false;
      onColumnVisibilityChange(newVisibility);
    }
  };

  // Update toggleSection to handle dropdown state
  const toggleSection = (sectionName: string) => {
    setOpenSection(prev => {
      // If clicking the same section, close it
      if (prev === sectionName) return null;
      // Otherwise, open the new section (closing any other open section)
      return sectionName;
    });
  };

  // Helper to get dropdown z-index based on which one is open
  const getDropdownZIndex = (sectionName: string) => {
    return openSection === sectionName ? "z-50" : "z-40";
  };

  // Update the dropdown buttons to use refs
  const setButtonRef = (name: string, element: HTMLButtonElement | null) => {
    buttonRefs.current[name] = element;
  };



  // Update the active filters display to be more compact
  const renderActiveFilters = () => {
    const hasActiveFilters = 
      Object.values(selectedFilters).some(f => f.length > 0) || 
      includeArchived !== "false" || 
      includeClosed || 
      filters.searchQuery || 
      filters.vulnerabilityType;

    if (!hasActiveFilters) return null;

    return (
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-y border-gray-100 bg-[#F9FAFB]/50">
        <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
          Active Filters:
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {filters.searchQuery && (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm">
              <span className="font-medium text-gray-500">Search:</span>
              <span>{cropSentence(filters.searchQuery, 20)}</span>
              <button
                onClick={() => {
                  setSearchQuery("");
                  onFilter({ ...filters, searchQuery: "" });
                }}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {filters.vulnerabilityType && (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm">
              <span className="font-medium text-gray-500">Vulnerability:</span>
              <span>{cropSentence(parseCategoryString(filters.vulnerabilityType), 20)}</span>
              <button
                onClick={() => {
                  setVulnerabilityType("");
                  onFilter({ ...filters, vulnerabilityType: "" });
                }}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {selectedFilters.programs.map(programId => {
            const program = programNames.find(p => p.id.toString() === programId);
            return (
              program && (
                <div
                  key={programId}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm"
                >
                  <span className="font-medium text-gray-500">Program:</span>
                  <span>{cropSentence(program.name, 20)}</span>
                  <button
                    onClick={() => handleProgramToggle(program.name)}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <FiTrash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            );
          })}
          {selectedFilters.states.map(state => (
            <div
              key={state}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm"
            >
              <span className="font-medium text-gray-500">Status:</span>
              <span>{state}</span>
              <button
                onClick={() => handleStateToggle(state)}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {selectedFilters.severityCategories.map(category => (
            <div
              key={category}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm"
            >
              <span className="font-medium text-gray-500">Severity:</span>
              <span>{category}</span>
              <button
                onClick={() => handleSeverityToggle(category)}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {selectedFilters.pentesters.map(username => (
            <div
              key={username}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm"
            >
              <span className="font-medium text-gray-500">Pentester:</span>
              <span>{cropSentence(username, 20)}</span>
              <button
                onClick={() => handlePentesterToggle(username)}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {includeArchived !== "false" && (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm">
              <span className="font-medium text-gray-500">Archived:</span>
              <span>{includeArchived === "only" ? "Only" : "Included"}</span>
              <button
                onClick={() => {
                  setIncludeArchived("false");
                  onFilter({ ...filters, includeArchived: "false" });
                }}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {includeClosed && (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700 shadow-sm">
              <span className="font-medium text-gray-500">Closed:</span>
              <span>Included</span>
              <button
                onClick={() => {
                  setIncludeClosed(false);
                  onFilter({ ...filters, includeClosed: false });
                }}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <button
            onClick={handleClearAll}
            className="ml-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
          >
            Clear all
          </button>
        </div>
      </div>
    );
  };

  const renderAddFilterMenu = () => {
    if (activeSubMenu === "program") {
      return (
        <div className="w-64">
          <div className="flex items-center gap-2 border-b border-gray-100 p-3">
            <button onClick={() => setActiveSubMenu(null)} className="text-gray-400 hover:text-gray-600">
              <FiChevronDown className="h-4 w-4 rotate-90" />
            </button>
            <span className="text-sm font-semibold text-gray-900">Program</span>
          </div>
          <div className="p-2">
            <div className="relative mb-2">
              <FiSearch className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search programs..."
                value={programSearchQuery}
                onChange={e => setProgramSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-0.5">
              {filteredPrograms.map(program => (
                <label key={program.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFilters.programs.includes(program.id.toString())}
                    onChange={() => handleProgramToggle(program.name)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 truncate">{program.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (activeSubMenu === "status") {
      return (
        <div className="w-56">
          <div className="flex items-center gap-2 border-b border-gray-100 p-3">
            <button onClick={() => setActiveSubMenu(null)} className="text-gray-400 hover:text-gray-600">
              <FiChevronDown className="h-4 w-4 rotate-90" />
            </button>
            <span className="text-sm font-semibold text-gray-900">Status</span>
          </div>
          <div className="p-2 max-h-60 overflow-y-auto space-y-0.5">
            {uniqueReportStates.map(state => (
              <label key={state} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedFilters.states.includes(state || "")}
                  onChange={() => handleStateToggle(state || "")}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{state}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeSubMenu === "severity") {
      return (
        <div className="w-56">
          <div className="flex items-center gap-2 border-b border-gray-100 p-3">
            <button onClick={() => setActiveSubMenu(null)} className="text-gray-400 hover:text-gray-600">
              <FiChevronDown className="h-4 w-4 rotate-90" />
            </button>
            <span className="text-sm font-semibold text-gray-900">Severity</span>
          </div>
          <div className="p-2 space-y-0.5">
            {uniqueSeverityCategories.map(category => (
              <label key={category} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedFilters.severityCategories.includes(category)}
                  onChange={() => handleSeverityToggle(category)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{category}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeSubMenu === "pentester") {
      return (
        <div className="w-64">
          <div className="flex items-center gap-2 border-b border-gray-100 p-3">
            <button onClick={() => setActiveSubMenu(null)} className="text-gray-400 hover:text-gray-600">
              <FiChevronDown className="h-4 w-4 rotate-90" />
            </button>
            <span className="text-sm font-semibold text-gray-900">Pentester</span>
          </div>
          <div className="p-2">
            <div className="relative mb-2">
              <FiSearch className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search pentesters..."
                value={pentesterSearchQuery}
                onChange={e => setPentesterSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-0.5">
              {filteredPentesters.map(username => (
                <label key={username} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFilters.pentesters.includes(username)}
                    onChange={() => handlePentesterToggle(username)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 truncate">{username}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="w-72">
        <div className="flex items-center justify-between border-b border-gray-100 p-3">
          <span className="text-sm font-semibold text-gray-900">Add Filter</span>
          <button onClick={() => setIsAddFilterOpen(false)} className="text-gray-400 hover:text-gray-600">
            <IoMdClose className="h-4 w-4" />
          </button>
        </div>
        <div className="py-1">
          <button
            onClick={() => setActiveSubMenu("program")}
            className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FaProjectDiagram className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">Program</div>
                <div className="text-xs text-gray-500">Filter by program</div>
              </div>
            </div>
            <FiChevronRight className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={() => setActiveSubMenu("status")}
            className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <FaClipboardList className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">Status</div>
                <div className="text-xs text-gray-500">Filter by status</div>
              </div>
            </div>
            <FiChevronRight className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={() => setActiveSubMenu("severity")}
            className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600">
                <FaExclamationTriangle className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">Severity</div>
                <div className="text-xs text-gray-500">Filter by severity</div>
              </div>
            </div>
            <FiChevronRight className="h-4 w-4 text-gray-400" />
          </button>
          {role === UserRole.ADMIN && (
            <button
              onClick={() => setActiveSubMenu("pentester")}
              className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                  <FaUserShield className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900">Pentester</div>
                  <div className="text-xs text-gray-500">Filter by pentester</div>
                </div>
              </div>
              <FiChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          )}
          <button
            onClick={handleSortOrderChange}
            className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                <FaSort className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">Date Sort</div>
                <div className="text-xs text-gray-500">{sortOrder === "desc" ? "Newest first" : "Oldest first"}</div>
              </div>
            </div>
          </button>

          {(role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA, UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER].includes(role)) && (
            <>
              <button
                onClick={() => {
                  const newValue = includeArchived === "false" ? "true" : includeArchived === "true" ? "only" : "false";
                  setIncludeArchived(newValue);
                  onFilter({ ...filters, includeArchived: newValue });
                }}
                className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <FaArchive className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">Archived</div>
                    <div className="text-xs text-gray-500">{includeArchived === "false" ? "Excluded" : includeArchived === "true" ? "Included" : "Only"}</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => {
                  const newValue = !includeClosed;
                  setIncludeClosed(newValue);
                  onFilter({ ...filters, includeClosed: newValue });
                }}
                className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                    <FaLock className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">Closed Reports</div>
                    <div className="text-xs text-gray-500">{includeClosed ? "Included" : "Excluded"}</div>
                  </div>
                </div>
              </button>
            </>
          )}
        </div>
        <div className="border-t border-gray-100 p-2">
          <button
            onClick={handleClearAll}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <FiTrash2 className="h-4 w-4" />
            <span>Clear all filters</span>
          </button>
        </div>
      </div>
    );
  };

  const activeFiltersCount = Object.values(selectedFilters).flat().filter(Boolean).length + 
    (includeArchived !== "false" ? 1 : 0) + 
    (includeClosed ? 1 : 0);

  return (
    <div className="w-full space-y-4">
      {/* Top Search and Action Bar */}
      <div className="flex items-center justify-between gap-4">
        {/* Search Input */}
        <div className="relative flex-1 group">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <FiSearch className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={handleSearch}
            className="block w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-16 text-sm placeholder-gray-400 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-4">
            <kbd className="hidden sm:inline-flex items-center rounded border border-gray-200 px-1.5 py-0.5 font-sans text-xs font-medium text-gray-400 bg-gray-50">
              /
            </kbd>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Add Filter Button */}
          <div className="relative" ref={addFilterRef}>
            <button
              onClick={() => setIsAddFilterOpen(!isAddFilterOpen)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all shadow-sm ${
                isAddFilterOpen
                  ? "border-blue-600 bg-blue-50 text-blue-600 ring-4 ring-blue-500/10"
                  : "border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
              }`}
            >
              <FiPlus className="h-4 w-4" />
              <span>Add Filter</span>
              {activeFiltersCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Add Filter Popover */}
            {isAddFilterOpen && (
              <div className="absolute right-0 top-full z-[100] mt-2 origin-top-right rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 animate-in fade-in zoom-in duration-200 overflow-hidden">
                {renderAddFilterMenu()}
              </div>
            )}
          </div>

          {/* Table Settings Button */}
          {role !== UserRole.RESEARCHER && (
            <div className="relative">
              <button
                onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
                className={`inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all shadow-sm hover:bg-gray-50 ${
                  isColumnMenuOpen ? "ring-4 ring-gray-500/10 border-gray-400" : ""
                }`}
              >
                <FiSettings className="h-4 w-4 text-gray-500" />
                <span>Table Settings</span>
              </button>

              {/* Table Settings Popover */}
              {isColumnMenuOpen && (
                <div
                  ref={columnsDropdownRef}
                  className="absolute right-0 top-full z-[100] mt-2 w-72 origin-top-right rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 animate-in fade-in zoom-in duration-200"
                >
                  <div className="px-3 py-2">
                    <h3 className="text-sm font-semibold text-gray-900">Table Settings</h3>
                    <p className="text-xs text-gray-500">Customize your view</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    {Object.entries({
                      createdAt: "Created At",
                      pentester: "Pentester",
                      action: "Actions",
                      assetType: "Asset Type"
                    }).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={columnVisibility[key as keyof ColumnVisibility]}
                          onChange={() => handleColumnVisibilityChange(key as keyof ColumnVisibility)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 border-t border-gray-100 p-2">
                    <button
                      onClick={() => {
                        onColumnVisibilityChange({
                          createdAt: true,
                          pentester: false,
                          action: true,
                          assetType: false
                        });
                      }}
                      className="w-full text-center py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      Reset to Default
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export Button */}
          {role && [
            UserRole.BUSINESS,
            UserRole.BUSINESS_ADMINISTRATOR,
            UserRole.ADMIN,
            UserRole.SUB_ADMIN,
            UserRole.ADMIN_MANAGER,
            UserRole.QA
          ].includes(role) && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`inline-flex items-center gap-2 rounded-xl bg-[#010D2C] px-4 py-2.5 text-sm font-semibold text-[#58CC02] transition-all shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isExporting ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#58CC02] border-t-transparent" />
              ) : (
                <FiDownload className="h-4 w-4 text-[#58CC02]" />
              )}
              <span>{isExporting ? "Exporting..." : "Export"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Active Filters Bar */}
      {renderActiveFilters()}
    </div>
  );
};

export default FilterSectionReports;
